import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Settings as SettingsIcon, 
  User,
  Lock,
  Bell,
  Globe,
  Save,
  Loader2,
  Building2,
  FileText
} from 'lucide-react';
import NavBar from '../components/NavBar';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsPage = () => {
  const { language } = useLanguage();
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [billingData, setBillingData] = useState({
    company_name: '',
    tax_office: '',
    tax_number: '',
    billing_address: '',
    city: '',
    postal_code: '',
    phone: ''
  });
  
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        full_name: user.full_name || '',
        email: user.email || ''
      }));
      fetchBillingInfo();
    }
  }, [user]);
  
  const fetchBillingInfo = async () => {
    try {
      const response = await axios.get(`${API}/auth/billing-info`);
      if (response.data) {
        setBillingData(prev => ({
          ...prev,
          ...response.data
        }));
      }
    } catch (error) {
      console.log('No billing info found');
    }
  };
  
  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  
  const handleBillingChange = (e) => {
    setBillingData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  
  const handleBillingUpdate = async (e) => {
    e.preventDefault();
    setBillingLoading(true);
    
    try {
      await axios.put(`${API}/auth/billing-info`, billingData);
      toast.success(language === 'tr' ? 'Fatura bilgileri güncellendi' : 'Billing info updated');
    } catch (error) {
      toast.error(language === 'tr' ? 'Güncelleme başarısız' : 'Update failed');
    } finally {
      setBillingLoading(false);
    }
  };
  
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.put(`${API}/users/me`, {
        full_name: formData.full_name
      });
      
      if (updateUser) {
        updateUser({ ...user, full_name: formData.full_name });
      }
      
      toast.success(language === 'tr' ? 'Profil güncellendi' : 'Profile updated');
    } catch (error) {
      toast.error(language === 'tr' ? 'Güncelleme başarısız' : 'Update failed');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (formData.new_password !== formData.confirm_password) {
      toast.error(language === 'tr' ? 'Şifreler eşleşmiyor' : 'Passwords do not match');
      return;
    }
    
    if (formData.new_password.length < 6) {
      toast.error(language === 'tr' ? 'Şifre en az 6 karakter olmalı' : 'Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.put(`${API}/users/me/password`, {
        current_password: formData.current_password,
        new_password: formData.new_password
      });
      
      toast.success(language === 'tr' ? 'Şifre değiştirildi' : 'Password changed');
      setFormData(prev => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: ''
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'tr' ? 'Şifre değiştirilemedi' : 'Failed to change password'));
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-slate-950" data-testid="settings-page">
      <NavBar />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center space-x-3 mb-8">
          <div className="p-3 rounded-lg bg-slate-800 text-slate-400">
            <SettingsIcon size={28} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">
              {language === 'tr' ? 'Ayarlar' : 'Settings'}
            </h1>
            <p className="text-slate-400">
              {language === 'tr' ? 'Hesap ayarlarınızı yönetin' : 'Manage your account settings'}
            </p>
          </div>
        </div>
        
        <div className="space-y-6">
          {/* Profile Settings */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <div className="flex items-center space-x-2">
                <User className="h-5 w-5 text-blue-400" />
                <CardTitle className="text-white">
                  {language === 'tr' ? 'Profil Bilgileri' : 'Profile Information'}
                </CardTitle>
              </div>
              <CardDescription className="text-slate-400">
                {language === 'tr' ? 'Kişisel bilgilerinizi güncelleyin' : 'Update your personal information'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProfileUpdate} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">{language === 'tr' ? 'Ad Soyad' : 'Full Name'}</Label>
                    <Input
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      className="bg-slate-800 border-slate-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Email</Label>
                    <Input
                      name="email"
                      value={formData.email}
                      disabled
                      className="bg-slate-800/50 border-slate-700 text-slate-400 mt-1"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  {language === 'tr' ? 'Kaydet' : 'Save'}
                </Button>
              </form>
            </CardContent>
          </Card>
          
          {/* Password Settings */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Lock className="h-5 w-5 text-amber-400" />
                <CardTitle className="text-white">
                  {language === 'tr' ? 'Şifre Değiştir' : 'Change Password'}
                </CardTitle>
              </div>
              <CardDescription className="text-slate-400">
                {language === 'tr' ? 'Hesap şifrenizi güncelleyin' : 'Update your account password'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <Label className="text-slate-300">{language === 'tr' ? 'Mevcut Şifre' : 'Current Password'}</Label>
                  <Input
                    type="password"
                    name="current_password"
                    value={formData.current_password}
                    onChange={handleChange}
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">{language === 'tr' ? 'Yeni Şifre' : 'New Password'}</Label>
                    <Input
                      type="password"
                      name="new_password"
                      value={formData.new_password}
                      onChange={handleChange}
                      className="bg-slate-800 border-slate-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">{language === 'tr' ? 'Şifre Tekrar' : 'Confirm Password'}</Label>
                    <Input
                      type="password"
                      name="confirm_password"
                      value={formData.confirm_password}
                      onChange={handleChange}
                      className="bg-slate-800 border-slate-700 text-white mt-1"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="bg-amber-600 hover:bg-amber-700">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
                  {language === 'tr' ? 'Şifreyi Değiştir' : 'Change Password'}
                </Button>
              </form>
            </CardContent>
          </Card>
          
          {/* Billing Information */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Building2 className="h-5 w-5 text-purple-400" />
                <CardTitle className="text-white">
                  {language === 'tr' ? 'Fatura Bilgileri' : 'Billing Information'}
                </CardTitle>
              </div>
              <CardDescription className="text-slate-400">
                {language === 'tr' ? 'Kurumsal fatura bilgilerinizi girin' : 'Enter your corporate billing information'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleBillingUpdate} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">{language === 'tr' ? 'Şirket/Kurum Adı' : 'Company Name'}</Label>
                    <Input
                      name="company_name"
                      value={billingData.company_name}
                      onChange={handleBillingChange}
                      placeholder={language === 'tr' ? 'ABC Teknoloji A.Ş.' : 'ABC Tech Inc.'}
                      className="bg-slate-800 border-slate-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">{language === 'tr' ? 'Vergi Dairesi' : 'Tax Office'}</Label>
                    <Input
                      name="tax_office"
                      value={billingData.tax_office}
                      onChange={handleBillingChange}
                      placeholder={language === 'tr' ? 'Kadıköy' : 'Tax Office'}
                      className="bg-slate-800 border-slate-700 text-white mt-1"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">{language === 'tr' ? 'Vergi No / TC Kimlik No' : 'Tax Number'}</Label>
                    <Input
                      name="tax_number"
                      value={billingData.tax_number}
                      onChange={handleBillingChange}
                      placeholder="1234567890"
                      className="bg-slate-800 border-slate-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">{language === 'tr' ? 'Telefon' : 'Phone'}</Label>
                    <Input
                      name="phone"
                      value={billingData.phone}
                      onChange={handleBillingChange}
                      placeholder="+90 555 123 4567"
                      className="bg-slate-800 border-slate-700 text-white mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">{language === 'tr' ? 'Fatura Adresi' : 'Billing Address'}</Label>
                  <Input
                    name="billing_address"
                    value={billingData.billing_address}
                    onChange={handleBillingChange}
                    placeholder={language === 'tr' ? 'Cadde No: 123' : '123 Main Street'}
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">{language === 'tr' ? 'Şehir' : 'City'}</Label>
                    <Input
                      name="city"
                      value={billingData.city}
                      onChange={handleBillingChange}
                      placeholder={language === 'tr' ? 'İstanbul' : 'Istanbul'}
                      className="bg-slate-800 border-slate-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">{language === 'tr' ? 'Posta Kodu' : 'Postal Code'}</Label>
                    <Input
                      name="postal_code"
                      value={billingData.postal_code}
                      onChange={handleBillingChange}
                      placeholder="34000"
                      className="bg-slate-800 border-slate-700 text-white mt-1"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={billingLoading} className="bg-purple-600 hover:bg-purple-700">
                  {billingLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
                  {language === 'tr' ? 'Fatura Bilgilerini Kaydet' : 'Save Billing Info'}
                </Button>
              </form>
            </CardContent>
          </Card>
          
          {/* Account Info */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Globe className="h-5 w-5 text-emerald-400" />
                <CardTitle className="text-white">
                  {language === 'tr' ? 'Hesap Bilgileri' : 'Account Information'}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-slate-800">
                  <span className="text-slate-400">{language === 'tr' ? 'Hesap Türü' : 'Account Type'}</span>
                  <span className="text-white capitalize">{user?.role?.replace('_', ' ') || 'User'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-800">
                  <span className="text-slate-400">{language === 'tr' ? 'Kayıt Tarihi' : 'Registered'}</span>
                  <span className="text-white">
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US') : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-slate-400">{language === 'tr' ? 'Dil Tercihi' : 'Language Preference'}</span>
                  <span className="text-white">{user?.preferred_language?.toUpperCase() || language.toUpperCase()}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default SettingsPage;
